// SGNL-194 — minimal safe Markdown → HTML renderer.
//
// Purpose: render the Next-Move draft (and similar AE-facing prose) with
// **bold**, *italic*, numbered/bulleted lists, and paragraph breaks —
// without pulling in a full markdown library and without ever passing
// untrusted text through `innerHTML` unescaped.
//
// Threat model: the draft is LLM-generated server-side; the BE
// `_coerce_markdown_text` already strips `<...>` runs as a defence-in-
// depth. This renderer additionally escapes every character in the
// input before applying any transformation, so even if the BE strip
// were defeated, no HTML reaches the output. The only HTML tags this
// function emits are a fixed set: <p>, <strong>, <em>, <code>, <ul>,
// <ol>, <li>, <br>.
//
// Out of scope (deliberately): links, headings, images, blockquotes,
// HTML pass-through. The AE doesn't need any of those for an email
// body / call agenda; supporting them would expand the threat surface
// for no benefit.

import { esc } from "./format";

/** Render a small markdown subset to safe HTML. */
export function renderSafeMarkdown(src: string): string {
  if (!src) return "";
  // 1) Escape first — every char that follows is now plain text.
  const escaped = esc(src);
  // 2) Normalise line endings; split into block-level chunks on blank lines.
  const normalised = escaped.replace(/\r\n?/g, "\n");
  const blocks = normalised.split(/\n{2,}/);
  return blocks.map(renderBlock).filter(Boolean).join("");
}

function renderBlock(block: string): string {
  const trimmed = block.trim();
  if (!trimmed) return "";
  const lines = trimmed.split("\n");
  // Numbered list: every line matches `^\d+\.\s`.
  if (lines.every((l) => /^\s*\d+\.\s+\S/.test(l))) {
    const items = lines.map((l) => l.replace(/^\s*\d+\.\s+/, ""));
    return `<ol>${items.map((it) => `<li>${renderInline(it)}</li>`).join("")}</ol>`;
  }
  // Bulleted list: every line matches `^[-*]\s`.
  if (lines.every((l) => /^\s*[-*]\s+\S/.test(l))) {
    const items = lines.map((l) => l.replace(/^\s*[-*]\s+/, ""));
    return `<ul>${items.map((it) => `<li>${renderInline(it)}</li>`).join("")}</ul>`;
  }
  // Otherwise: paragraph. Single line breaks inside become <br>.
  const inner = lines.map(renderInline).join("<br>");
  return `<p>${inner}</p>`;
}

function renderInline(text: string): string {
  // The text is already HTML-escaped. We use markers that can't appear
  // in HTML-escaped output (asterisks/underscores survive `esc()`).
  let out = text;
  // **bold** — non-greedy, paired markers only.
  out = out.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  // Q-01 (PR #112): neutralise any LONE remaining `**` so the italic
  // regex below can't pick up an unmatched marker and wrap content in
  // <em>. Render as a literal entity-safe sequence — `&#42;&#42;` —
  // which displays as `**` but is opaque to the inline-formatting
  // regexes that follow. Sole-asterisk markers (single `*`) are
  // handled by the italic regex's whitespace boundaries.
  out = out.replace(/\*\*/g, "&#42;&#42;");
  // *italic* and _italic_ — single markers, paired, no whitespace right
  // after opener / before closer (avoids partial matches like "5 * 4").
  out = out.replace(/(^|[\s(])\*(\S(?:[^*]*?\S)?)\*(?=[\s).,!?:;]|$)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[\s(])_(\S(?:[^_]*?\S)?)_(?=[\s).,!?:;]|$)/g, "$1<em>$2</em>");
  // `code` — backtick-wrapped inline code.
  out = out.replace(/`([^`]+?)`/g, "<code>$1</code>");
  return out;
}
